// @ts-no-check

// Vendor imports
const Cookies = require('js-cookie')
const _ = require('lodash')
// Protobuf imports
const protobufjs = require('protobufjs/minimal')
protobufjs.util.Long = require('long') // Support for 64 bit values
const pb = require('messages/client/protobuf')
const pbPageContextUtils = require('messages/shared/page-context')(pb.globals)
// Client lib imports
const airbrake = require('client/lib/airbrake')
const { send: ajax } = require('client/lib/request')
// Sibling imports
const pageContextUtils = require('./page-context')

/** @typedef { import('bowser').Parser.Parser } BowserParser */
/** @typedef { import('./types.d').PageContext } PageContext */
/** @typedef { import('./types.d').PageContextWithReferrer } PageContextWithReferrer */

/** @typedef {Object.<string, string>} UserExperiments */
/** @typedef {Object.<string, boolean>} UserRollouts */

/** @typedef { keyof typeof import('messages/client/protobuf.d').commands } ProtobufCommandName */
/** @typedef { import('messages/client/protobuf.d').globals.IExperiment } ProtobufExperiment */
/** @typedef { import('messages/client/protobuf.d').globals.IPageContext } ProtobufPageContext */

/**
 * @typedef {Object} CreateCommandContextOptions
 * @property {BowserParser} browser
 * @property {string} buildNumber
 * @property {string} [commandUrl]
 * @property {number} eventTimestamp
 * @property {UserExperiments} experiments
 * @property {PageContextWithReferrer} pageContext
 * @property {UserRollouts} rollouts
 * @property {string} sessionId
 */

/**
 * @typedef {Object} CommandContextPageContexts
 * @property {ProtobufPageContext} currentContext
 * @property {ProtobufPageContext|void} referrerContext
 */

/**
 * @param {CreateCommandContextOptions} options
 */
function createCommandContext(options) {
  return pb.commands.CommandContext.fromObject({
    ...convertPageContext(options),
    eventTimestamp: options.eventTimestamp,
    session: {
      sessionId: options.sessionId,
      device: {
        deviceType: getDeviceType(options.browser),
        userAgent: navigator.userAgent
      },
      appVersion: {
        appType: 'WEBAPP',
        buildNumber: options.buildNumber
      }
    }
  })
}

/**
 * @param {ProtobufCommandName} commandName
 * @param {any} commandParams
 */
function getCommandJSON(commandName, commandParams) {
  const commandClass = pb.commands[commandName]
  if (!commandClass) {
    throw new Error(`Invalid Protobuf commandName[${commandName}]`)
  }
  const normalizedParams = normalizeCommandParams(commandName, commandParams)
  const commandInstance = commandClass.fromObject(normalizedParams)
  const verifyMessage = commandClass.verify(commandInstance)
  if (verifyMessage) {
    const err = new Error(`protobufjs command[${commandName}] verify failed`)
    airbrake.notify({
      error: err,
      params: {
        commandName,
        commandParams,
        normalizedParams,
        verifyMessage
      }
    })
  }
  return commandInstance.toJSON()
}

/**
 * @param {ProtobufCommandName} commandName
 * @param {any} params
 * @param {CreateCommandContextOptions} options
 */
async function sendCommand(commandName, params, options) {
  const context = createCommandContext(options)
  const data = getCommandJSON(commandName, { context, ...params })
  if (_.get(context, 'currentContext.page') === 'ADMIN_PAGE') {
    // eslint-disable-next-line no-console
    console.dir({ commandName, data }, { depth: null })
    return
  }
  await ajax({
    url: options.commandUrl || `/api/v0.1/commands/${_.kebabCase(commandName)}`,
    method: 'post',
    contentType: 'application/json',
    data,
    sessionId: options.sessionId,
    includePageContext: false // It's already in the command JSON
  })
}

module.exports = {
  sendCommand
}

/**
 * @param {ProtobufCommandName} commandName
 * @param {any} params
 * @returns {any}
 */
function normalizeCommandParams(commandName, params) {
  switch (commandName) {
    case 'TrackExternalMixShareEvent':
    case 'TrackExternalUrlPrivateShareEvent':
    case 'TrackExternalUserShareEvent':
      return {
        ...params,
        shareType: pbPageContextUtils.convertShareType(params.shareType)
      }
    case 'TrackStreamProfileView':
      return {
        ...params,
        streamType: pbPageContextUtils.convertStreamType(params.streamType)
      }
    default:
      return params
  }
}


/**
 * @param {Object} options
 * @param {PageContextWithReferrer} options.pageContext
 * @param {UserRollouts} options.rollouts
 * @param {UserExperiments} options.experiments
 * @returns {CommandContextPageContexts}
 */
function convertPageContext(options) {
  // Convert experiments/rollouts to protobuf-style
  const rollouts = Object.keys(options.rollouts)
  /** @type {ProtobufExperiment[]} */
  const experiments = (
    Object.entries(options.experiments).map(([ name, group ]) => ({ name, group }))
  )
  // Unpack PageContextWithReferrer into two separate (old-style) page contexts
  const currentPageContext = pageContextUtils.unpackCurrentPageContext(options.pageContext)
  const referrerPageContext = pageContextUtils.unpackReferrerPageContext(options.pageContext)
  // Convert old-style page context into protobuf-style PageContext
  /** @type {ProtobufPageContext} */
  const currentContext = {
    ...pbPageContextUtils.convertPageContext(currentPageContext),
    experiments,
    rollouts
  }
  /** @type {ProtobufPageContext|void} */
  let referrerContext = null
  // Don't send `referrerContext` if there is no referrer page name
  if (referrerPageContext['page_name']) {
    referrerContext = {
      ...pbPageContextUtils.convertPageContext(referrerPageContext),
      experiments,
      rollouts
    }
  }
  return { currentContext, referrerContext }
}

/**
 * @param {BowserParser} browser
 * @returns {string}
 */
function getDeviceType(browser) {
  if (!browser) {
    return 'UNKNOWN_DEVICE'
  }
  /**
   * iPads report themselves as Desktop Safari as of iOS 13, so we set a
   * `device_override` cookie after we detect Apple's lying user-agent.
   * See: `client-v2/common/state/env/actions.js` module `detectBrowser` function
   */
  if (Cookies.get('device_override') === 'iPad') {
    return 'IPAD'
  }
  const osName = browser.getOSName()
  const platformType = browser.getPlatformType()
  if (osName === 'iOS') {
    return platformType === 'tablet' ? 'IPAD' : 'IPHONE'
  }
  if (osName === 'Android') {
    return platformType === 'tablet' ? 'ANDROID_TABLET' : 'ANDROID'
  }
  if (platformType === 'desktop') {
    return 'DESKTOP'
  }
}
